import { SortingState } from '@tanstack/react-table';

import { EMainProductCodeType } from '@aqx-core/common/api/config';
import { EOrderHistoryType } from '@aqx-core/common/api/order';
import { EColumnPreferenceOrderType, EOrderTableColumn } from '@aqx-core/common/api/user-account-setting';
import {
  COLUMN_ID_TO_EXPORT_COLUMN_MAP,
  EOrderColumn,
  EOrderDealHistoriesColumn,
  EOrderHistoriesColumn,
  EOrderPendingColumn
} from '@aqx-core/common/constants/order';
import { getUserTimeZone } from '@aqx-core/common/dayjs';
import { i18n } from '@aqx-core/common/i18n';
import { accountColumnSettingService } from '@aqx-core/common/services/account/column-preference';
import { companyService } from '@aqx-core/common/services/company';
import { convertDateRangeWithUtcOffset } from '@aqx-core/common/services/order/utils';

import { TOrderExportColumn } from './types';

export const getOrderExportColumnList = (orderType: EColumnPreferenceOrderType): TOrderExportColumn[] => {
  switch (orderType) {
    case EColumnPreferenceOrderType.OPEN_POSITION:
      return Object.values(EOrderColumn);
    case EColumnPreferenceOrderType.PENDING_ORDER:
      return Object.values(EOrderPendingColumn);
    case EColumnPreferenceOrderType.ORDER_HISTORY:
    case EColumnPreferenceOrderType.POSITION_HISTORY:
    case EColumnPreferenceOrderType.TRANSACTION_HISTORY:
      return Object.values(EOrderHistoriesColumn);
    case EColumnPreferenceOrderType.ORDERS_AND_DEALS:
      return Object.values(EOrderDealHistoriesColumn);
    default:
      return Object.values(EOrderColumn);
  }
};

export const mapTableColumnToExportColumn = (tableColumn: EOrderTableColumn | string): string => {
  const mapped = COLUMN_ID_TO_EXPORT_COLUMN_MAP[tableColumn];
  if (mapped) {
    return mapped;
  }

  // Fallback: if column is already in UPPER_SNAKE_CASE format, return as-is
  const columnStr = String(tableColumn);
  if (columnStr === columnStr.toUpperCase() && columnStr.includes('_')) {
    return columnStr;
  }

  // Last resort: convert to uppercase
  return columnStr.toUpperCase();
};

export const getHiddenColumnsFromPreferences = (orderType: EColumnPreferenceOrderType): string[] => {
  const { data } = accountColumnSettingService.context;
  const typePreference = data[orderType];

  // Get hidden columns from current settings
  const currentSettings = typePreference?.currentSettings || [];
  const hiddenColumns = currentSettings.filter((setting) => !setting.visible).map((setting) => setting.label);

  // Map table column names to export column names and apply special MT5 logic
  return hiddenColumns.map((column) => {
    const exportColumn = mapTableColumnToExportColumn(column);

    // Special case, map SIZE or VOLUME to VOLUME_SIZE
    if (exportColumn === 'SIZE' || exportColumn === 'VOLUME') {
      return 'VOLUME_SIZE';
    }

    return exportColumn;
  });
};

export const mapSortingToExportColumn = (
  sorting: SortingState | undefined,
  columnList: TOrderExportColumn[]
): { sortingColumn: TOrderExportColumn | undefined; ascending: boolean } => {
  if (!sorting || sorting.length === 0) {
    return { sortingColumn: undefined, ascending: false };
  }

  const sortedColumn = sorting[0];
  const ascending = !sortedColumn.desc; // TanStack: desc = false means ascending

  const exportColumnName = mapTableColumnToExportColumn(sortedColumn.id);

  const sortingColumn = columnList.find((col) => col === exportColumnName);

  return { sortingColumn, ascending };
};

export const getOrderExportBaseParams = (orderType: EColumnPreferenceOrderType, tableSorting?: SortingState) => {
  const columnList = getOrderExportColumnList(orderType);
  const hiddenColumns = getHiddenColumnsFromPreferences(orderType);

  let sortingColumn: TOrderExportColumn | undefined;
  let ascending: boolean;

  if (tableSorting && tableSorting.length > 0) {
    const mapped = mapSortingToExportColumn(tableSorting, columnList);
    sortingColumn = mapped.sortingColumn;
    ascending = mapped.ascending;
  } else {
    sortingColumn = undefined;
    ascending = false;
  }

  const baseParams = {
    ascending,
    timeZoneId: getUserTimeZone(),
    hiddenColumns
  };

  // For Open Positions , Pending Orders don't include date ranges or type
  if (
    orderType === EColumnPreferenceOrderType.OPEN_POSITION ||
    orderType === EColumnPreferenceOrderType.PENDING_ORDER
  ) {
    // Only include sortingColumn if it has a value
    if (sortingColumn) {
      return {
        ...baseParams,
        sortingColumn
      };
    }

    return baseParams;
  }

  // For history exports (Position History, Order History, Orders & Deals), include date ranges
  const now = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(now.getDate() - 30);

  const { epochFrom, epochTo } = convertDateRangeWithUtcOffset({
    from: thirtyDaysAgo,
    to: now
  });

  const baseParamsWithDates = {
    ...baseParams,
    from: epochFrom!,
    to: epochTo!
  };

  // For Orders & Deals export, exclude sortingColumn for now, backend will enhance later
  if (orderType === EColumnPreferenceOrderType.ORDERS_AND_DEALS) {
    return baseParamsWithDates;
  }

  // For other history types (Position History), include sortingColumn if it has a value
  if (sortingColumn) {
    return {
      ...baseParamsWithDates,
      sortingColumn
    };
  }

  return baseParamsWithDates;
};

// Utility functions for date range and notifications
export const getDefaultDateRange = () => {
  const now = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(now.getDate() - 30);

  return convertDateRangeWithUtcOffset({
    from: thirtyDaysAgo,
    to: now
  });
};

export const getOrderHistoryTypeFromSelectedTab = (
  orderHistorySelectedTab: EColumnPreferenceOrderType
): EOrderHistoryType => {
  switch (orderHistorySelectedTab) {
    case EColumnPreferenceOrderType.POSITION_HISTORY:
      // For MT4 and Horizon, use ORDER type instead of POSITION
      const companyIntegrationType = companyService.getCompanyIntegration();
      if (
        companyIntegrationType === EMainProductCodeType.HORIZON ||
        companyIntegrationType === EMainProductCodeType.METATRADER4
      ) {
        return EOrderHistoryType.ORDER;
      }
      return EOrderHistoryType.POSITION;
    case EColumnPreferenceOrderType.ORDER_HISTORY:
      return EOrderHistoryType.ORDER;
    case EColumnPreferenceOrderType.ORDERS_AND_DEALS:
      // This tab uses deal export, but if called from here, default to DEAL
      return EOrderHistoryType.DEAL;
    case EColumnPreferenceOrderType.TRANSACTION_HISTORY:
      return EOrderHistoryType.POSITION;
    default:
      // Default fallback for backward compatibility
      return EOrderHistoryType.ORDER;
  }
};

export const createExportResult = (success: boolean): { success: boolean; message: string } => {
  return {
    success,
    message: success ? i18n.t('Export completed successfully.') : i18n.t('Export failed. Please try again.')
  };
};
